import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Modal } from "antd";

import { ApiError, api, describeApiError } from "@/core/api/client";
import { useT } from "@/core/i18n";
import { useSession } from "@/core/state/session";
import { LegalReader } from "@/shared/legal/LegalReader";

export function LegalReadPrompt() {
  const t = useT();
  const identity = useSession((state) => state.identity);
  const tenant = identity?.tenantSlug ?? "";
  const cache = useQueryClient();
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<"read" | "later" | null>(null);
  const [error, setError] = useState("");
  const [closed, setClosed] = useState(false);
  const query = useQuery({
    queryKey: ["legal-reads-pending", tenant, identity?.userId],
    enabled: Boolean(tenant),
    retry: false,
    queryFn: () => api.legalPendingReads(tenant),
  });
  const documents = query.data?.items ?? [];
  const complete = documents.length > 0 && documents.every((document) => ready[document.version_id]);

  async function confirm() {
    setBusy("read");
    setError("");
    try {
      await api.legalAccept(tenant, documents.map((document) => document.version_id));
      setClosed(true);
      await cache.invalidateQueries({ queryKey: ["legal-reads-pending", tenant] });
    } catch (reason) {
      setError(reason instanceof ApiError ? describeApiError(reason, t("legal.promptFailed")) : t("legal.promptFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function later() {
    if (documents.length === 0) {
      setClosed(true);
      return;
    }
    setBusy("later");
    setError("");
    try {
      await api.legalDeferReads(tenant, documents.map((document) => document.version_id));
      setClosed(true);
      await cache.invalidateQueries({ queryKey: ["legal-reads-pending", tenant] });
    } catch (reason) {
      setError(reason instanceof ApiError ? describeApiError(reason, t("legal.laterFailed")) : t("legal.laterFailed"));
    } finally {
      setBusy(null);
    }
  }

  if (!tenant || closed || query.isLoading || query.isError || documents.length === 0) return null;

  return (
    <Modal
      className="legal-read-prompt"
      title={t("legal.promptTitle")}
      open
      width={720}
      footer={null}
      maskClosable={false}
      onCancel={() => void later()}
      okButtonProps={{ style: { display: "none" } }}
      cancelButtonProps={{ style: { display: "none" } }}
    >
      <p className="form-intro">{t("legal.promptLead")}</p>
      {documents.map((document) => (
        <LegalReader
          key={document.version_id}
          document={document}
          label={t("legal.confirmReadItem", { title: document.title, version: document.version_number })}
          onChange={(value) => setReady((current) => ({ ...current, [document.version_id]: value }))}
        />
      ))}
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <p className="legal-read-later-hint">{t("legal.laterHint")}</p>
      <div className="legal-read-prompt-actions">
        <Button onClick={() => void later()} loading={busy === "later"} disabled={busy === "read"}>{t("legal.later")}</Button>
        <Button type="primary" onClick={() => void confirm()} loading={busy === "read"} disabled={!complete || busy === "later"}>{t("legal.confirmRead")}</Button>
      </div>
    </Modal>
  );
}
